import { useState } from 'react';
import { Check, ChevronDown, Filter, ListFilter } from 'lucide-react';
import type { ImportacionStatus, MonedaImportacion } from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import { DatePicker } from '@/shared/components/ui/date-picker';
import { Label } from '@/shared/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';

export type FiltroImportaciones = {
  status: Set<ImportacionStatus>;
  moneda: Set<MonedaImportacion>;
  /** yyyy-mm-dd sobre la fecha de la importación; vacío = sin límite. */
  desde: string;
  hasta: string;
};

const ESTADOS: ImportacionStatus[] = ['Abierta', 'Cerrada', 'Anulada'];
const MONEDAS: Array<{ value: MonedaImportacion; label: string }> = [
  { value: 'USD', label: 'Dólares' },
  { value: 'ARS', label: 'Pesos Argentinos' },
];

/** Las anuladas quedan fuera salvo que se pidan. */
export const FILTRO_INICIAL: FiltroImportaciones = {
  status: new Set(['Abierta', 'Cerrada']),
  moneda: new Set(['USD', 'ARS']),
  desde: '',
  hasta: '',
};

export function FiltrarPopover({
  value,
  onChange,
  onReset,
  active,
}: {
  value: FiltroImportaciones;
  onChange: (next: FiltroImportaciones) => void;
  onReset: () => void;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);

  const alternar = <T,>(set: Set<T>, v: T) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    return next;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('h-12 gap-2', active && 'border-foreground/60')}>
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active && <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 p-3">
        <div className="space-y-1.5">
          <Titulo>Fecha</Titulo>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Desde</Label>
              <DatePicker
                value={value.desde}
                onChange={(next) => onChange({ ...value, desde: next })}
                placeholder="dd/mm/yyyy"
                max={value.hasta || undefined}
                ariaLabel="Fecha desde"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Hasta</Label>
              <DatePicker
                value={value.hasta}
                onChange={(next) => onChange({ ...value, hasta: next })}
                placeholder="dd/mm/yyyy"
                min={value.desde || undefined}
                ariaLabel="Fecha hasta"
              />
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Titulo>Estado</Titulo>
          <div className="flex flex-col gap-1">
            {ESTADOS.map((s) => (
              <Opcion
                key={s}
                label={s}
                checked={value.status.has(s)}
                onToggle={() => onChange({ ...value, status: alternar(value.status, s) })}
              />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Titulo>Moneda</Titulo>
          <div className="flex flex-col gap-1">
            {MONEDAS.map((m) => (
              <Opcion
                key={m.value}
                label={m.label}
                checked={value.moneda.has(m.value)}
                onToggle={() => onChange({ ...value, moneda: alternar(value.moneda, m.value) })}
              />
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onReset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <ListFilter className="h-3.5 w-3.5" aria-hidden /> {children}
    </p>
  );
}

function Opcion({ label, checked, onToggle }: { label: string; checked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition-colors',
        checked
          ? 'border-foreground bg-foreground/5 text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      <span>{label}</span>
      {checked && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
    </button>
  );
}
